import { isNullOrWhiteSpace } from "@civicplus/preamble-ui/lib/Utilities/StringHelper";
import { EmailEvent, MessageHistory, MessageType } from "../entities/Message";
import { ODataQuery } from "../entities/ODataQuery";
import { Paged } from "../entities/Paged";
import { stringHasValue } from "../views/admin/shared/functions";
import { ApiService } from "./ApiService";
import { IEmailActivityDetails, IEmailEventStatistics } from "../util/EmailMessageActivityDataTypes";
import { ISentMessageStatistics, ISmsActivityDetails } from "../util/SmsActivityDataTypes";

export interface ISentMessage {
    month: number;
    day: number;
    year: number;
    type: MessageType;
    sent: number;
}

export class MessageService {
    private readonly apiService: ApiService;

    constructor(apiService: ApiService) {
        this.apiService = apiService;
    }

    public async getMessageHistory(
        page = 0,
        rowsPerPage = 25,
        listId: number | null,
        sortColumn = "id",
        sortDirection = "desc",
        filter?: unknown
    ): Promise<Paged<MessageHistory>> {
        const odata = new ODataQuery({
            top: rowsPerPage,
            skip: page * rowsPerPage,
            orderBy: sortColumn,
            orderByDirection: sortDirection,
            filter
        });

        return (await this.apiService.getOdata(`messages/history/${listId ?? ""}`, odata)) as Paged<MessageHistory>;
    }

    public async getMessageDetails(messageId: number, listId: number | null): Promise<MessageHistory> {
        let url = `messages/history/details/${messageId}`;

        if (listId !== null) {
            url += `?listId=${listId}`;
        }

        return (await this.apiService.get(url)) as MessageHistory;
    }

    public async getSentMessages(type: number, startDateUtc: Date, endDateUtc: Date): Promise<Paged<MessageHistory>> {
        return (await this.apiService.get(
            `messages/stats/sent?type=${type}&startDateUtc=${startDateUtc.toJSON()}&endDateUtc=${endDateUtc.toJSON()}`
        )) as Paged<MessageHistory>;
    }

    public async getMessageEventsForEmail(
        email: string,
        top: number,
        skip: number,
        shardName?: string
    ): Promise<Paged<EmailEvent>> {
        let url = `messages/events/email?$top=${top}&$skip=${skip}&$filter=to eq '${encodeURIComponent(email)}'&$orderby=time desc&$count=true`;

        if (stringHasValue(shardName)) {
            url += `&shardName=${shardName}`;
        }

        return (await this.apiService.getNoOrg(url)) as Paged<EmailEvent>;
    }

    public async getMessageEventsData(messageId: string, type: string, shardName?: string): Promise<EmailEvent[]> {
        let url = `messages/events/email/message/${messageId}?$top=1&$filter=type eq '${encodeURIComponent(type)}'&$select=Data`;
        if (stringHasValue(shardName)) {
            url += `&shardName=${shardName}`;
        }

        return (await this.apiService.getNoOrg(url)) as EmailEvent[];
    }

    public async getSentMessageCounts(messageId: number, listId?: number): Promise<ISentMessageStatistics> {
        let url = `messages/stats/${messageId}/count`;

        if (listId) {
            url = `${url}?listId=${listId}`;
        }

        return (await this.apiService.get(url)) as ISentMessageStatistics;
    }

    public async getMessageActivityDetails(
        messageId: number,
        externalId: string,
        eventType: string | undefined
    ): Promise<IEmailActivityDetails[]> {
        if (!isNullOrWhiteSpace(eventType) && eventType === "email_bounced") {
            return (await this.apiService.get(
                `messages/stats/${messageId}/activity/${externalId}/email?$top=100&$select=Data`
            )) as IEmailActivityDetails[];
        }
        return (await this.apiService.get(
            `messages/stats/${messageId}/activity/${externalId}/email?$top=100`
        )) as IEmailActivityDetails[];
    }

    public async getMessageActivityDetailsByTag(tag: string): Promise<unknown[]> {
        return (await this.apiService.get(`messages/stats/activity/tag/${tag}`)) as unknown[];
    }

    public async getSmsMessageActivityDetails(messageId: number, externalId: string): Promise<ISmsActivityDetails[]> {
        return (await this.apiService.get(
            `messages/stats/${messageId}/activity/${externalId}/sms?$top=100`
        )) as ISmsActivityDetails[];
    }

    public async getSentMessageChartStats(messageId: number, listId?: number): Promise<IEmailEventStatistics[]> {
        let url = `messages/stats/${messageId}/chart`;
        if (listId) {
            url = `${url}?listId=${listId}`;
        }

        const resp = (await this.apiService.get(url)) as Array<{
            month: number;
            day: number;
            year: number;
            type: string;
            count: number;
        }>;

        if (!resp) {
            return [];
        }

        // Sort chronologically so the chart's x-axis is in date order
        const sorted = [...resp].sort((a, b) => a.year - b.year || a.month - b.month || a.day - b.day);

        const formatted = await Promise.all(
            sorted.map((x) => {
                return {
                    date: new Date(`${x.month}/${x.day}/${x.year}`).toLocaleDateString(),
                    type: x.type,
                    count: x.count
                };
            })
        );

        return (await this.groupByDate(formatted)) as IEmailEventStatistics[];
    }

    public async getSentMessageActivity(
        messageId: number,
        listId?: number | null,
        page = 0,
        rowsPerPage = 25,
        sortColumn = "time",
        sortDirection = "desc",
        filter?: unknown
    ): Promise<Paged<IEmailActivityDetails>> {
        const odata = new ODataQuery({
            top: rowsPerPage,
            skip: page * rowsPerPage,
            orderBy: sortColumn,
            orderByDirection: sortDirection,
            filter
        });

        let url = `messages/stats/${messageId}/activity/email`;

        if (listId) {
            url = `${url}?listId=${listId}`;
        }

        return (await this.apiService.getOdata(url, odata)) as Paged<IEmailActivityDetails>;
    }

    public async getSentMessageActivitySms(
        messageId: number,
        listId: number | null,
        page = 0,
        rowsPerPage = 25,
        sortColumn = "time",
        sortDirection = "desc",
        filter?: unknown
    ): Promise<Paged<ISmsActivityDetails>> {
        const odata = new ODataQuery({
            top: rowsPerPage,
            skip: page * rowsPerPage,
            orderBy: sortColumn,
            orderByDirection: sortDirection,
            filter
        });

        let url = `messages/stats/${messageId}/activity/sms`;

        if (listId) {
            url = `${url}?listId=${listId}`;
        }

        return (await this.apiService.getOdata(url, odata)) as Paged<ISmsActivityDetails>;
    }

    public async getSentMessagesHistory(startDateUtc: Date, endDateUtc: Date): Promise<unknown> {
        const resp = (await this.apiService.get(
            `messages/stats/history?startDateUtc=${startDateUtc.toJSON()}&endDateUtc=${endDateUtc.toJSON()}`
        )) as Array<ISentMessage>;

        // Sort chronologically so the chart's x-axis is in date order
        const sorted = [...resp].sort((a, b) => a.year - b.year || a.month - b.month || a.day - b.day);

        const formatted = await Promise.all(
            sorted.map((x) => {
                return {
                    date: new Date(`${x.month}/${x.day}/${x.year}`).toLocaleDateString(),
                    type: x.type,
                    sent: x.sent
                };
            })
        );

        return await this.groupByDate(formatted);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async groupByDate(arrayObjects: any[]) {
        return await arrayObjects.reduce(async (promise, currentObject) => {
            const result = await promise;

            if (result) {
                const val = currentObject["date"];
                result[val] = result[val] || [];
                result[val].push(currentObject);
            }

            return result;
        }, Promise.resolve([]));
    }
}
